// SAT model-test blueprint stored on Quiz.metadata. Mirrors toeflBlueprint's API so the quiz
// Add/Edit forms and the taker treat SAT uniformly. A SAT test has two sections — Reading and
// Writing, then Math — each split into two separately-timed modules. Per-module time is derived
// from the question count: Reading and Writing = 1.1×#questions, Math = 1.35×#questions (minutes).

export const SAT_SECTIONS = [
    { key: 'reading_writing', name: 'Reading and Writing', maxModules: 2, navigation: 'back_within_module', minutesPerQuestion: 1.1, defaultMinutes: 30 },
    { key: 'math', name: 'Math', maxModules: 2, navigation: 'back_within_module', minutesPerQuestion: 1.35, defaultMinutes: 30 },
];

export const sectionByName = (name) => SAT_SECTIONS.find(s => s.name === name);

// Stable bucket keys (both sections are multi-module → always "Name — Module N").
export function bucketKey(sectionName, moduleIdx) {
    const sec = sectionByName(sectionName);
    const multi = sec && sec.maxModules > 1;
    return multi ? `${sectionName} — Module ${moduleIdx + 1}` : sectionName;
}
export function parseBucketKey(bucket) {
    const m = /^(.*) — Module (\d+)$/.exec(bucket);
    if (m) return { sectionName: m[1], moduleIdx: Number(m[2]) - 1 };
    return { sectionName: bucket, moduleIdx: 0 };
}

export function bucketsFor(moduleCount) {
    const out = [];
    SAT_SECTIONS.forEach(sec => {
        const count = sec.maxModules === 1 ? 1 : (moduleCount[sec.key] || 2);
        for (let i = 0; i < count; i += 1) out.push({ sectionKey: sec.key, sectionName: sec.name, moduleIdx: i, count, key: bucketKey(sec.name, i, count) });
    });
    return out;
}

// SAT is auto-graded MCQ (plus optional student-produced-response as TEXT). One task per module
// is enough; group only holds passage/stimulus grouping.
export function taskInfo(q) {
    if (q.question_type === 'TEXT') return { task: 'text', title: q.topic || 'Student response' };
    return { task: 'mcq', title: q.topic || 'Multiple choice' };
}

export function origTaskByQid(metadata) {
    const map = {};
    (metadata?.sections || []).forEach((sec) => {
        (sec.modules || []).forEach((mod) => {
            (mod.tasks || []).forEach((t, i) => {
                const id = `${sec.key || sec.name}:${mod.key || mod.name}:${i}`;
                (t.question_ids || []).forEach((qid) => {
                    map[qid] = { _id: id, task: t.task, title: t.title, group_id: t.group_id };
                });
            });
        });
    });
    return map;
}

export function buildTasks(questionObjs, origByQid) {
    const tasks = [];
    const origTaskByKey = {};
    (questionObjs || []).forEach((q) => {
        const orig = origByQid && origByQid[q.question_id];
        if (orig) {
            const key = `orig:${orig._id}`;
            let task = origTaskByKey[key];
            if (!task) {
                task = { _key: key, task: orig.task, title: orig.title, question_ids: [] };
                if (orig.group_id !== undefined) task.group_id = orig.group_id;
                origTaskByKey[key] = task;
                tasks.push(task);
            }
            task.question_ids.push(q.question_id);
            return;
        }
        const info = taskInfo(q);
        const groupKey = q.group_id ? `new:g${q.group_id}` : `new:t${info.task}:${q.topic || ''}`;
        const last = tasks[tasks.length - 1];
        if (last && last._key === groupKey) {
            last.question_ids.push(q.question_id);
        } else {
            const task = { _key: groupKey, task: info.task, title: info.title, question_ids: [q.question_id] };
            if (q.group_id) task.group_id = q.group_id;
            tasks.push(task);
        }
    });
    return tasks.map(({ _key, ...t }) => t);
}

const moduleName = (idx) => `Module ${idx + 1}`;

// Per-module minutes from the question count (R&W 1.1×, Math 1.35×), rounded up, min 1.
function moduleMinutes(sec, count) {
    return Math.max(1, Math.ceil((sec.minutesPerQuestion || 1) * count));
}

// Build the full blueprint + per-section question_ids (for QuizQuestionSet) + total minutes.
// Durations are computed from question counts, not taken from the form (SAT rule).
export function buildBlueprint({ moduleCount, durations, questionsByBucket, existingMetadata }) {
    void durations; // SAT computes per-module minutes from question counts, ignoring manual input.
    const origByQid = existingMetadata ? origTaskByQid(existingMetadata) : null;
    const questionIdsBySection = {};
    let totalMinutes = 0;
    const sections = SAT_SECTIONS.map((sec) => {
        const count = sec.maxModules === 1 ? 1 : (moduleCount[sec.key] || 2);
        const modules = [];
        const secIds = [];
        for (let i = 0; i < count; i += 1) {
            const bucket = bucketKey(sec.name, i, count);
            const qObjs = questionsByBucket[bucket] || [];
            if (qObjs.length === 0) continue;
            secIds.push(...qObjs.map(q => q.question_id));
            const mins = moduleMinutes(sec, qObjs.length);
            totalMinutes += mins;
            modules.push({ key: `m${i + 1}`, name: moduleName(i), duration_minutes: mins, tasks: buildTasks(qObjs, origByQid) });
        }
        if (secIds.length) questionIdsBySection[sec.name] = secIds;
        const out = { key: sec.key, name: sec.name, modules };
        if (sec.navigation) out.navigation = sec.navigation;
        return out;
    }).filter(s => s.modules.length > 0);

    return { blueprint: { test_kind: 'sat_model_test', sections }, questionIdsBySection, totalMinutes };
}

export function bucketsFromBlueprint(metadata, questionsBySection) {
    const allById = {};
    Object.values(questionsBySection || {}).forEach(arr => (arr || []).forEach(q => { allById[q.question_id] = q; }));
    const questionsByBucket = {};
    const moduleCount = {};
    const durations = {};
    (metadata?.sections || []).forEach((sec) => {
        const count = (sec.modules || []).length || 1;
        moduleCount[sec.key] = count;
        (sec.modules || []).forEach((mod, idx) => {
            const bucket = bucketKey(sec.name, idx, count);
            const qids = (mod.tasks || []).flatMap(t => t.question_ids || []);
            questionsByBucket[bucket] = qids.map(id => allById[id]).filter(Boolean);
            durations[bucket] = mod.duration_minutes;
        });
    });
    return { questionsByBucket, moduleCount, durations };
}
